/**
 * useNotifications Hook
 * 
 * P2 Implementation: Crude but functional notification retrieval
 * Fetches user's notifications from Firestore in real-time
 * 
 * Exit Condition: Notifications exist as data, even if delivery is crude
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot, Timestamp, writeBatch, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase';
import type { Notification, NotificationStatus } from '@/types/notification';

interface UseNotificationsOptions {
  userId: string | undefined;
  status?: NotificationStatus;
  limit?: number;
}

interface UseNotificationsReturn {
  notifications: Notification[];
  loading: boolean;
  error: Error | null;
  unreadCount: number;
  markAllAsRead: () => Promise<void>;
  clearAll: () => Promise<void>;
  searchNotifications: (searchQuery: string) => Notification[];
}

/**
 * Hook to fetch user's notifications in real-time
 * 
 * @param options - User ID and optional filters
 * @returns Notifications array, loading state, error, and unread count
 * 
 * @example
 * ```tsx
 * const { notifications, loading, unreadCount } = useNotifications({ 
 *   userId: user.uid,
 *   status: 'unread',
 *   limit: 10 
 * });
 * ```
 */
export function useNotifications(options: UseNotificationsOptions): UseNotificationsReturn {
  const { userId, status, limit: maxLimit = 50 } = options;
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Reset state if no user
    if (!userId) {
      setNotifications([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    // Build query
    const notificationsRef = collection(db, 'notifications');
    let q = query(
      notificationsRef,
      where('recipientId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(maxLimit)
    );

    // Add status filter if provided
    if (status) {
      q = query(
        notificationsRef,
        where('recipientId', '==', userId),
        where('status', '==', status),
        orderBy('createdAt', 'desc'),
        limit(maxLimit)
      );
    }

    // Subscribe to real-time updates
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const notifs = (snapshot.docs ?? []).map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            recipientId: data.recipientId,
            type: data.type,
            title: data.title,
            body: data.body,
            actorId: data.actorId,
            actorName: data.actorName,
            resourceType: data.resourceType,
            resourceId: data.resourceId,
            actionType: data.actionType,
            status: data.status,
            priority: data.priority,
            url: data.url,
            createdAt: data.createdAt as Timestamp,
            readAt: data.readAt as Timestamp | null,
            archivedAt: data.archivedAt as Timestamp | null,
            deliveryChannels: data.deliveryChannels,
          } as Notification;
        // Exclude archived notifications — they are soft-deleted and should not
        // appear in the inbox. The Firestore query intentionally fetches ALL statuses
        // to avoid requiring an extra composite index; archived items are dropped here.
        }).filter((n) => n.status !== 'archived');

        setNotifications(notifs);
        setLoading(false);
      },
      (err) => {
        console.error('[useNotifications] Error fetching notifications:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    // Cleanup subscription
    return () => unsubscribe();
  }, [userId, status, maxLimit]);

  // Calculate unread count
  const unreadCount = notifications.filter((n) => n.status === 'unread').length;

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!userId) return;
    const unread = notifications.filter((n) => n.status === 'unread');
    if (unread.length === 0) return;
    const batch = writeBatch(db);
    unread.forEach((n) => {
      batch.update(doc(db, 'notifications', n.id), {
        status: 'read',
        readAt: serverTimestamp(),
      });
    });
    await batch.commit();
  }, [userId, notifications]);

  // Archive all notifications (clear all)
  const clearAll = useCallback(async () => {
    if (!userId) return;
    const active = notifications.filter((n) => n.status !== 'archived');
    if (active.length === 0) return;
    const batch = writeBatch(db);
    active.forEach((n) => {
      batch.update(doc(db, 'notifications', n.id), {
        status: 'archived',
        archivedAt: serverTimestamp(),
      });
    });
    await batch.commit();
  }, [userId, notifications]);

  // Client-side search — matches on title, body, or type (case-insensitive substring)
  const searchNotifications = useCallback((searchQuery: string): Notification[] => {
    if (!searchQuery.trim()) return notifications;
    const term = searchQuery.toLowerCase();
    return notifications.filter(
      (n) =>
        n.title?.toLowerCase().includes(term) ||
        n.body?.toLowerCase().includes(term) ||
        n.type?.toLowerCase().includes(term)
    );
  }, [notifications]);

  return {
    notifications,
    loading,
    error,
    unreadCount,
    markAllAsRead,
    clearAll,
    searchNotifications,
  };
}

/**
 * Mark a notification as read
 * 
 * P2: Direct Firestore update (no Cloud Function)
 * Users can update their own notifications per security rules
 */
export async function markNotificationAsRead(notificationId: string): Promise<void> {
  const { updateDoc, doc, serverTimestamp } = await import('firebase/firestore');
  
  const notificationRef = doc(db, 'notifications', notificationId);
  await updateDoc(notificationRef, {
    status: 'read',
    readAt: serverTimestamp(),
  });
}

/**
 * Archive a notification
 * 
 * P2: Direct Firestore update (no Cloud Function)
 */
export async function archiveNotification(notificationId: string): Promise<void> {
  const { updateDoc, doc, serverTimestamp } = await import('firebase/firestore');
  
  const notificationRef = doc(db, 'notifications', notificationId);
  await updateDoc(notificationRef, {
    status: 'archived',
    archivedAt: serverTimestamp(),
  });
}
